import { GridApi } from '@ag-grid-community/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import React from 'react';
import { ENTITIES } from '../../constants';
import { NotificationGroups } from '../../store/notifications/types';
import AgGrid, { AgGridContext, UserCellRenderer } from '../AgGrid';
import { NotificationTableRowData } from './InboxContent';
import { NotificationTableActionRenderer } from './NotificationTableActionRenderer';
import { NotificationTableBreadcrumbRenderer } from './NotificationTableBreadcrumbRenderer';

interface InboxNotificationTableProps {
  rowData: NotificationTableRowData[];
  notificationGroup: NotificationGroups;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableWrapper: {
      width: '100%',
      height: '100%',
      [theme.breakpoints.up('sm')]: {
        // intiallay hide the notification table row action button
        '& .notification-table-action': {
          display: 'none',
        },
        // display the notification table row action button while row hover
        '& .ag-row-hover .notification-table-action': {
          display: 'initial',
        },
      },
    },
  }),
);

export const InboxNotificationTable = ({
  rowData = [],
  notificationGroup,
}: InboxNotificationTableProps) => {
  const classes = useStyles();
  const [gridApi, setGridApi] = React.useState<GridApi>();

  const agGridContextValue = React.useMemo(
    () => ({
      gridApi,
      setGridApi,
    }),
    [gridApi, setGridApi],
  );

  const columns = React.useMemo(
    () => [
      {
        headerName: 'Name',
        field: 'fileName',
        suppressSizeToFit: true,
      },
      {
        headerName: 'Location',
        field: 'fileLocation',
        minWidth: 300,
        cellRenderer: 'fileLocationRenderer',
        cellRendererParams: {
          useCellData: true,
        },
      },
      {
        headerName:
          notificationGroup === ENTITIES.NOTIFY_FILE_UPLOADED
            ? 'From'
            : 'Signed by',
        field: 'userName',
        cellRenderer: 'userCellRenderer',
        minWidth: 280,
        cellRendererParams: {
          useCellData: true,
          fieldKeys: {
            nameField: 'userName',
            avatarField: 'picture',
          },
        },
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        cellRenderer: 'actionRenderer',
        suppressSizeToFit: true,
        cellRendererParams: {
          useCellData: true,
        },
        cellClass: 'centeredColumn',
        pinned: 'right',
      },
    ],
    [],
  );

  return (
    <div className={classes.tableWrapper} data-testid="notification-table">
      <AgGridContext.Provider value={agGridContextValue}>
        <AgGrid
          rowData={rowData}
          columnDefs={columns}
          overlayParams={{
            noRowsLabel: 'No notification',
          }}
          getRowNodeId={(row: NotificationTableRowData) => row.id}
          frameworkComponents={{
            userCellRenderer: UserCellRenderer,
            fileLocationRenderer: NotificationTableBreadcrumbRenderer,
            actionRenderer: NotificationTableActionRenderer,
          }}
          imutable
        />
      </AgGridContext.Provider>
    </div>
  );
};
